import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Skeleton,
  ThemeProvider,
  createTheme,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { bonusApi } from "../../Components/bonusApi";
import { getProgramsList, getCoursesList } from "../marketing/marketingApi";
import { useTheme } from "../../hooks/useTheme";
import { createMuiThemeFromMode } from "../../theme/theme";
import styles from "../AllEnquiries.module.scss";
import { RoleContext } from "../../Contexts/RoleContext";

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
};

function BonusProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { role } = React.useContext(RoleContext);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    metric: "conversion",
    goal: "",
    scope_type: "overall",
    scope_id: "",
    start_date: "",
    end_date: "",
    reward_type: "cash",
    reward_description: "",
  });

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    bonusApi
      .getById(id)
      .then(setData)
      .catch((err) => {
        toast.error(err?.message || "Failed to load bonus");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!editOpen) return;
    getProgramsList().then((d) => setPrograms(Array.isArray(d) ? d : [])).catch(() => setPrograms([]));
  }, [editOpen]);

  useEffect(() => {
    if (!editOpen) return;
    if (editForm.scope_type === "program" && editForm.scope_id) {
      getCoursesList(Number(editForm.scope_id)).then((d) => setCourses(Array.isArray(d) ? d : [])).catch(() => setCourses([]));
    } else if (editForm.scope_type === "course") {
      getCoursesList(null).then((d) => setCourses(Array.isArray(d) ? d : [])).catch(() => setCourses([]));
    } else {
      setCourses([]);
    }
  }, [editOpen, editForm.scope_type, editForm.scope_id]);

  const bonus = data?.bonus;
  const formatProgress = (b) => {
    if (!b) return "";
    if (b.metric === "conversion_rate") return `${Number(b.current_progress).toFixed(1)}%`;
    if (b.metric === "revenue") return Number(b.current_progress).toLocaleString();
    return String(Math.round(b.current_progress));
  };
  const formatGoal = (b) => {
    if (!b) return "";
    if (b.metric === "conversion_rate") return `${Number(b.goal).toFixed(0)}%`;
    if (b.metric === "revenue") return Number(b.goal).toLocaleString();
    return String(Math.round(b.goal));
  };

  const canEdit = ["admin", "manager"].includes((role || "").toLowerCase()) &&
    ["draft", "pending_approval"].includes((bonus?.status || "").toLowerCase());

  const openEdit = () => {
    if (!bonus) return;
    setEditForm({
      name: bonus.name || "",
      description: bonus.description || "",
      metric: bonus.metric || "conversion",
      goal: bonus.goal != null ? String(bonus.goal) : "",
      scope_type: bonus.scope_type || "overall",
      scope_id: bonus.scope_id != null ? String(bonus.scope_id) : "",
      start_date: String(bonus.start_date || "").slice(0, 10),
      end_date: String(bonus.end_date || "").slice(0, 10),
      reward_type: bonus.reward_type || "cash",
      reward_description: bonus.reward_description || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!bonus?.id) return;
    if (!editForm.name.trim()) {
      toast.warning("Bonus name is required.");
      return;
    }
    setSaving(true);
    try {
      await bonusApi.update(bonus.id, {
        name: editForm.name.trim(),
        description: (editForm.description || "").trim() || null,
        metric: editForm.metric,
        goal: Number(editForm.goal),
        scope_type: editForm.scope_type,
        scope_id: editForm.scope_type === "overall" ? null : Number(editForm.scope_id),
        start_date: editForm.start_date,
        end_date: editForm.end_date,
        reward_type: editForm.reward_type,
        reward_description: (editForm.reward_description || "").trim() || null,
      });
      toast.success("Bonus updated.");
      const refreshed = await bonusApi.getById(bonus.id);
      setData(refreshed);
      setEditOpen(false);
    } catch (err) {
      toast.error(err?.message || "Failed to update bonus.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper}>
          <Box sx={{ mb: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/bonuses")} sx={{ mb: 1 }}>
              Back to Bonuses
            </Button>
          </Box>

          {loading && (
            <Box>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
              <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
            </Box>
          )}

          {!loading && !bonus && (
            <Typography color="text.secondary">Bonus not found.</Typography>
          )}

          {!loading && bonus && (
            <>
              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ bgcolor: "primary.main", color: "primary.contrastText", p: 1.5, borderRadius: 2 }}>
                      <EmojiEventsIcon fontSize="large" />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={600}>{bonus.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {bonus.metric_label} · {bonus.start_date} to {bonus.end_date}
                      </Typography>
                    </Box>
                  </Box>
                  {canEdit && (
                    <Box sx={{ mb: 2 }}>
                      <Button variant="outlined" onClick={openEdit}>
                        Edit Bonus
                      </Button>
                    </Box>
                  )}
                  {bonus.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {bonus.description}
                    </Typography>
                  )}
                  <Typography variant="h6" gutterBottom>
                    Progress: {formatProgress(bonus)} / {formatGoal(bonus)}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, bonus.percentage || 0)}
                    sx={{ borderRadius: 1, height: 12, mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Scope: {bonus.scope_display ?? (bonus.scope_type === "overall" ? "Overall" : "—")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reward: {bonus.reward_description || bonus.reward_type}
                  </Typography>
                </CardContent>
              </Card>

              <Typography variant="h6" sx={{ mb: 1 }}>Top Contributors</Typography>
              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <List dense>
                  {(data.leaderboard || []).length === 0 ? (
                    <ListItem>
                      <ListItemText primary="No contributions yet." />
                    </ListItem>
                  ) : (
                    (data.leaderboard || []).map((row, idx) => (
                      <ListItem key={row.staff_id || idx}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: "primary.main" }}>
                            {row.display_name?.charAt(0) || (idx + 1)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${idx + 1}. ${row.display_name || "Unknown"}`}
                          secondary={
                            bonus.metric === "conversion_rate"
                              ? `${row.total_value}%`
                              : bonus.metric === "revenue"
                                ? row.total_value?.toLocaleString()
                                : `${row.total_value} conversion(s)`
                          }
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </Card>

              <Typography variant="h6" sx={{ mb: 1 }}>Contribution Timeline</Typography>
              <Card sx={{ borderRadius: 2 }}>
                <List dense>
                  {(data.timeline || []).length === 0 ? (
                    <ListItem>
                      <ListItemText primary="No activity yet." />
                    </ListItem>
                  ) : (
                    (data.timeline || []).map((item) => (
                      <ListItem key={item.id}>
                        <ListItemText
                          primary={`${item.staff_name} contributed ${item.metric_label?.toLowerCase()} → +${item.value}${bonus.metric === "conversion_rate" ? "%" : ""}`}
                          secondary={item.created_at}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </Card>
            </>
          )}
        </Paper>
      </Box>
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Bonus</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bonus Name"
                value={editForm.name}
                onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={editForm.description}
                onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Metric</InputLabel>
                <Select
                  value={editForm.metric}
                  label="Metric"
                  onChange={(e) => setEditForm((p) => ({ ...p, metric: e.target.value }))}
                >
                  <MenuItem value="conversion">Conversions</MenuItem>
                  <MenuItem value="revenue">Revenue</MenuItem>
                  <MenuItem value="conversion_rate">Conversion Rate</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Goal"
                value={editForm.goal}
                onChange={(e) => setEditForm((p) => ({ ...p, goal: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Scope</InputLabel>
                <Select
                  value={editForm.scope_type}
                  label="Scope"
                  onChange={(e) => setEditForm((p) => ({ ...p, scope_type: e.target.value, scope_id: "" }))}
                >
                  <MenuItem value="overall">Overall</MenuItem>
                  <MenuItem value="program">Program</MenuItem>
                  <MenuItem value="course">Course</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {editForm.scope_type === "program" && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Program</InputLabel>
                  <Select
                    value={editForm.scope_id}
                    label="Program"
                    onChange={(e) => setEditForm((p) => ({ ...p, scope_id: e.target.value }))}
                  >
                    {programs.map((p) => (
                      <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {editForm.scope_type === "course" && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Course</InputLabel>
                  <Select
                    value={editForm.scope_id}
                    label="Course"
                    onChange={(e) => setEditForm((p) => ({ ...p, scope_id: e.target.value }))}
                  >
                    {courses.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={editForm.start_date}
                onChange={(e) => setEditForm((p) => ({ ...p, start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={editForm.end_date}
                onChange={(e) => setEditForm((p) => ({ ...p, end_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Reward Type</InputLabel>
                <Select
                  value={editForm.reward_type}
                  label="Reward Type"
                  onChange={(e) => setEditForm((p) => ({ ...p, reward_type: e.target.value }))}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="gift">Gift</MenuItem>
                  <MenuItem value="event">Event</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reward Description"
                value={editForm.reward_description}
                onChange={(e) => setEditForm((p) => ({ ...p, reward_description: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default BonusProfilePage;
